import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { resolveApiError, useTranslation } from '@eon/i18n';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  toast,
} from '@eon/ui';
import { hooks, type OmUser } from '@eon/api-client';

interface EditOmUserDialogProps {
  user: OmUser | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface FormValues {
  name: string;
  email: string;
  phoneNumber: string;
}

export function EditOmUserDialog({ user, open, onOpenChange }: EditOmUserDialogProps) {
  const { t } = useTranslation();
  const update = hooks.useOmUpdateUser();
  const { register, handleSubmit, reset, formState } = useForm<FormValues>({
    defaultValues: { name: '', email: '', phoneNumber: '' },
  });

  useEffect(() => {
    if (user && open) {
      reset({
        name: user.name ?? '',
        email: user.email ?? '',
        phoneNumber: user.phone ?? '',
      });
    }
  }, [user, open, reset]);

  const onSubmit = handleSubmit(async (values) => {
    if (!user) return;
    try {
      await update.mutateAsync({
        userId: user.userId,
        name: values.name.trim() || undefined,
        email: values.email.trim() || undefined,
        phoneNumber: values.phoneNumber.trim() || undefined,
      });
      toast.success(t('user.updated'));
      onOpenChange(false);
    } catch (err) {
      toast.error(resolveApiError(err, t));
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('portal.users.editTitle')}</DialogTitle>
        </DialogHeader>
        {user ? (
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="edit-name">{t('user.name')}</Label>
              <Input id="edit-name" {...register('name', { required: true })} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-email">{t('user.email')}</Label>
              <Input id="edit-email" type="email" {...register('email')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-phone">{t('auth.phone')}</Label>
              <Input id="edit-phone" {...register('phoneNumber')} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t('actions.cancel')}
              </Button>
              <Button type="submit" disabled={formState.isSubmitting || update.isPending}>
                {t('actions.save')}
              </Button>
            </DialogFooter>
          </form>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
